<script lang="ts" setup>
import { ref, watch } from "vue";
import { getEnergyLabelStatus } from "@/services/api/chart.api";
import TableExpirationDateAge from "@/components/general/TableExpirationDateAge.vue";
import ChartBuildingEnergyLabels from "@/components/app/charts/ChartBuildingEnergyLabels.vue";
import { useToast } from "vue-toastification";

const toaster = useToast();

const props = defineProps({
  token: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  showAge: {
    type: Boolean,
    default: true,
  },
  partyType: {
    type: String,
    required: true,
  },
  labelSource: {
    type: String,
  },
});

const labelData = ref({});
const tableData = ref({
  zeroToThreeYears: 0,
  fourToSevenYears: 0,
  sevenYears: 0,
  eightYears: 0,
  nineYears: 0,
  expired: 0,
  noLabel: 0,
  totalNumberOfBuildings: 0,
});
const isLoading = ref(true);

watch(
  () => props.token,
  () => {
    if (props.token) {
      fetchBuildingEnergyLabelStatusData();
    } else {
      tableData.value = { zeroToThreeYears: 0, fourToSevenYears: 0, sevenYears: 0, eightYears: 0, nineYears: 0, expired: 0, noLabel: 0, totalNumberOfBuildings: 0 };
    }
  },
  { immediate: true }
);

async function fetchBuildingEnergyLabelStatusData() {
  isLoading.value = true;
  const response = await getEnergyLabelStatus(props.token, props.partyType, props.labelSource);

  if (response) {
    tableData.value = response.data;
    // N.B.: Dit gebruik van segmentParser() zorgt ervoor dat de volgorde waarin de labels
    // door de back-end waren gezet verloren gaat. Als het wenselijk is om de data te
    // presenteren in de volgorde waarin ze door de back-end worden aangeleverd, dan
    // is het zaak om over response.data.labels als array te itereren bij het aanmaken van
    // de keys en values arrays, en niet als object.
    // Aangezien er wel wat voor te zeggen is om het sorteren in de front-end te doen,
    // is de huidige aanpak niet echt een probleem, dus laat ik het maar zo, maar het kan
    // dus wel eleganter.
    labelData.value = segmentParser(response.data.labels, "labelCategory");
    isLoading.value = false;
  } else {
    isLoading.value = false;
    toaster.error("Ophalen van de buildingEnergyLabelStatus data is mislukt!");
  }
}

function segmentParser(segments: any[], key = "text", value = "value") {
  let parsedData: Record<string, any> = {};
  let chartSegments = [...segments].sort((a, b) => {
    const left = a?.[key];
    const right = b?.[key];

    if (typeof left === "string" && typeof right === "string") {
      return left.localeCompare(right);
    }

    if (left === right) {
      return 0;
    }

    return left > right ? 1 : -1;
  });

  chartSegments.forEach((chartSegment) => {
    parsedData[chartSegment[key]] = chartSegment[value];
  });

  return parsedData;
}
</script>

<template>
  <div class="widget-item" :class="{ 'widget-item--compact': props.compact, 'widget-item--labels-only': !props.showAge, 'widget-item--homes': props.partyType === 'Homes' }">
    <header>
      <h2>Status van labels</h2>
      <div class="widget-actions">
        <information-dropdown id="status-labels">
          <template v-slot:header>
            <h2>Status van labels</h2>
          </template>
          <p>
            Hier worden de labels en leeftijd getoond van de energielabels van al uw {{ props.partyType == "Homes" ?
              "complexen" :
              "gebouwen" }}. Een energielabel laat de energieprestatie van een
            {{ props.partyType == "Homes" ? "woning" : "gebouw" }} zien. Ook maakt het energielabel duidelijk welke
            energiebesparende maatregelen mogelijk zijn. De labelklasse voor
            {{ props.partyType == "Homes" ? "woningen" : "utiliteitsbouw" }} loopt van A++++ t/m G, van weinig naar
            veel
            besparingsmogelijkheden.
          </p>
        </information-dropdown>
      </div>
    </header>
    <loader-spinner v-if="isLoading" :is-loading="isLoading" :min-height="200"></loader-spinner>
    <v-container v-else class="widget-wrapper pa-0">
      <v-row no-gutters>
        <v-col :cols="props.showAge ? 6 : 12">
          <v-sheet :class="{ 'mr-6': !props.compact }">
            <chart-building-energy-labels :party-type="props.partyType" :source-data="labelData" />
          </v-sheet>
        </v-col>
        <v-col v-if="props.showAge" cols="6">
          <v-sheet :class="{ 'ml-2': !props.compact }">
            <h3>Leeftijd labels</h3>
            <TableExpirationDateAge :table-data="tableData"></TableExpirationDateAge>
          </v-sheet>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<style scoped>
.widget-item--compact .widget-wrapper {
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
  height: calc(100% - 48px);
}

.widget-item--compact :deep(.v-row),
.widget-item--compact :deep(.v-col),
.widget-item--compact :deep(.v-sheet) {
  height: 100%;
}

.widget-item--compact :deep(.energylabels-wrapper) {
  max-height: 100%;
}

.widget-item--compact :deep(.label-overview-container-large) {
  font-size: 0.8rem;
}

.widget-item--compact :deep(.label-overview-container-large .object-count) {
  font-size: 0.72rem;
}

.widget-item--compact h3 {
  margin-top: 0;
  margin-bottom: 4px;
  min-height: 0;
  font-size: 1.5rem;
}

.widget-item--compact :deep(.label-overview-container-large .labeldata-small .v-col) {
  padding-top: 1px;
  padding-bottom: 0;
}

.widget-item--compact :deep(.label-overview-container-large .text-medium-emphasis) {
  margin-bottom: 2px;
  line-height: 1.15;
}

.widget-item--compact :deep(.label-overview-container-large .labeldata-small) {
  margin: 0;
  min-height: 24px;
}

.widget-item--labels-only :deep(.label-overview-container-large .labeldata-small) {
  min-height: 28px;
}

.widget-item--labels-only :deep(.label-overview-container-large .labeldata-small .v-col) {
  padding-top: 2px;
  padding-bottom: 2px;
}

.widget-item--compact :deep(.label-overview-container-large .v-progress-linear) {
  height: 8px !important;
}

.widget-item--compact :deep(.energy-label-wrap) {
  font-size: 0.72rem;
  height: 23px;
  width: 54px;
  padding: 0 3px;
}

.widget-item--compact :deep(.energy-label-wrap .icon-energy-label.color-a-plus-plus-plus-plus + .energy-label-text),
.widget-item--compact :deep(.energy-label-wrap .icon-energy-label.color-a-plus-plus-plus-plus-plus + .energy-label-text) {
  font-size: 0.58rem;
  letter-spacing: -0.2px;
}

.widget-item--compact :deep(.icon-energy-label::before) {
  font-size: 25px !important;
}

.widget-item--compact :deep(.overview-table) {
  font-size: 0.92rem;
}

.widget-item--compact :deep(.overview-table .v-data-table__td),
.widget-item--compact :deep(.overview-table .v-data-table__th) {
  padding-top: 2px;
  padding-bottom: 2px;
}

.widget-item--homes h3 {
  margin-bottom: 4px;
  font-size: 1.75rem;
}
</style>
